import { Bank, Gender, PersonalDetailsForm } from '@ha/domain';
import { maxLength } from '@ha/hypotheken-afspraak.constants';

import { Component, inject, input, OnInit } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TitleCasePipe } from '@angular/common';

import { ErrorMessages } from '@ap/form/errors';
import { ApContentModule, ApContentService } from '@ap/content';
import { ApAnalyticsModule } from '@ap/analytics';
import { ApRadioButtonComponent, ApRadioGroupComponent } from '@ap/radio';
import { ApInputComponent } from '@ap/input';
import { ApDateInputComponent } from '@ap/input/date';

@Component({
    selector: 'personal-details',
    templateUrl: './personal-details.component.html',
    standalone: true,
    imports: [
        ApAnalyticsModule,
        ApContentModule,
        FormsModule,
        ReactiveFormsModule,
        ApRadioGroupComponent,
        ApRadioButtonComponent,
        ApDateInputComponent,
        ApInputComponent
    ]
})
export class PersonalDetailsComponent implements OnInit {
    private readonly contentService = inject(ApContentService);
    private readonly titleCasePipe = inject(TitleCasePipe);

    protected readonly bank = Bank;
    protected readonly gender = Gender;

    readonly personalDetailsForm = input.required<FormGroup<PersonalDetailsForm>>();

    todayDate: Date;

    ngOnInit() {
        this.todayDate = this.normalizeToUTCMidnight(new Date());
    }

    getMaxDate(): Date {
        const defaultDate = new Date();
        defaultDate.setUTCFullYear(this.todayDate.getUTCFullYear() - 18, this.todayDate.getMonth(), this.todayDate.getDate());

        return this.normalizeToUTCMidnight(defaultDate);
    }

    getMinDate(): Date {
        const defaultDate = new Date();
        defaultDate.setUTCFullYear(this.todayDate.getUTCFullYear() - 100, this.todayDate.getMonth(), this.todayDate.getDate());

        return this.normalizeToUTCMidnight(defaultDate);
    }

    getErrorMessage(contentKey: string): ErrorMessages {
        const label: string = this.contentService.get(contentKey).toLowerCase();
        return {
            required: this.contentService.get('general.error.required', {
                fieldName: label
            }),
            maxlength: this.contentService.get('general.error.max', {
                fieldName: label,
                number: maxLength.default.toString()
            }),
            incorrect: this.contentService.get('general.error.incorrect', {
                fieldName: label
            })
        } as ErrorMessages;
    }

    transformValue(formControlName: string): void {
        const control = this.personalDetailsForm().get(formControlName);
        if (!control) {
            return;
        }

        const valueNoCap: string = control.value ?? '';

        const newValue = formControlName === 'initials' ? this.formatInitials(valueNoCap) : this.titleCasePipe.transform(valueNoCap);

        control.setValue(newValue);
    }

    private formatInitials(value: string): string {
        return `${value
            .split('')
            .filter((char) => char !== '.' && char !== ' ')
            .map((char) => char.toLocaleUpperCase())
            .join('.')}.`;
    }

    private normalizeToUTCMidnight(date: Date): Date {
        if (!date || !(date instanceof Date)) {
            return null;
        }

        const copyDate = new Date(date);
        copyDate.setUTCHours(0, 0, 0, 0);

        return copyDate;
    }
}
